import { useState } from 'react';
import styled from 'styled-components';
import { ReactComponent as DownloadIcon } from '@elementary/common-ui/assets/download.svg';
import {
  FlexColumn,
  FlexSpace,
  Button,
  FlexCenter,
  TestLineageNavButton,
} from '@elementary/common-ui/components';
import { Typography } from '@mui/material';
import DependenciesTable from 'shared/components/DependenciesTable';
import { DependenciesFilterOptions } from 'shared/components/DependenciesTable/types';
import useReport from 'shared/hooks/useReport';
import PanelError from 'shared/components/PanelError';
import useDependenciesData from '../hooks/useDependenciesData';

const Wrapper = styled(FlexColumn)`
  background-color: ${({ theme }) => theme.palette.common.gray15};
  padding: 16px 24px;
  gap: 16px;
  border-radius: 8px;
  height: 100%;
  justify-content: center;
`;

type Props = {
  tableAssetId: string;
  testName: string;
  resource: {
    type: string;
    name: string;
  };
};

const Dependencies = ({ tableAssetId, testName, resource }: Props) => {
  const [filter, setFilter] = useState<DependenciesFilterOptions>(
    DependenciesFilterOptions.All,
  );
  const [includeLatestExecution, setIncludeLatestExecution] = useState(false);

  const { data: reportData } = useReport();

  const { data, isLoading, onCSVDownload, getColumnName, error } =
    useDependenciesData({
      tableAssetId,
      resource,
      filter,
      includeLatestExecution,
    });
  const columnName = getColumnName(resource.name);

  return (
    <Wrapper>
      {error ? (
        <PanelError
          error={error as Error}
          errorName="ApiError"
          page={'Dependencies'}
        />
      ) : (
        <>
          <FlexSpace sx={{ alignItems: 'center' }}>
            <Typography sx={{ fontSize: '16px', fontWeight: 500 }}>
              Related Assets
            </Typography>
            <FlexCenter sx={{ gap: 1 }}>
              {!!reportData && (
                <TestLineageNavButton
                  modelId={tableAssetId}
                  testName={testName}
                  columnName={
                    resource.type === 'COLUMN' ? columnName : undefined
                  }
                  buttonStyle="button"
                  disabled={isLoading}
                >
                  Show in lineage
                </TestLineageNavButton>
              )}
              <Button
                variant="outlined"
                onClick={onCSVDownload}
                startIcon={<DownloadIcon width={12} />}
                disabled={isLoading}
              >
                CSV
              </Button>
            </FlexCenter>
          </FlexSpace>
          <DependenciesTable
            modelId={tableAssetId}
            data={data}
            loading={isLoading}
            resource={resource}
            filter={filter}
            setFilter={(f) => {
              setFilter(f as DependenciesFilterOptions);
            }}
            onShowLatestExecution={(val) => setIncludeLatestExecution(val)}
          />
        </>
      )}
    </Wrapper>
  );
};

export default Dependencies;
